import React from "react";
import styled from "styled-components";
import { Table, Tag, Typography, Button } from "@arco-design/web-react";
import EmptyState from "../Components/EmptyState";
import { suppressionMeta } from "./contactStatus";
import { formatSort, parseSort, signedUpDate } from "./contactColumns";
import { formatCustom, formatDate } from "./contactHistory";


import "react-csv-importer/dist/index.css";

const { Text } = Typography;

// MUI -> Arco. The MUI Table/TableHead/TableBody/TableRow/TableCell JSX is
// rewritten into an Arco Table columns config + data source. makeStyles ->
// styled-components. Row selection moves from per-row MUI Checkboxes to Arco's
// rowSelection; the "select all visible" header checkbox is rowSelection's
// header checkbox. Row click (ignoring checkbox clicks) is wired through onRow.
// Columns come from contactColumns.js (the user picks which show); sorting is
// server-side, since rows page in as you scroll — a header click asks the
// parent to reload in the new order.
// Chip -> Tag.
// The table scrolls itself, both ways (Arco's `scroll`): the header stays put
// going down, the checkboxes + first column stay put going sideways, and Arco
// shades an edge when there are more columns past it. More rows load when the
// table's own scroll area nears the bottom.

// ------------------------
// Table stuff
// ------------------------

const TABLE_HEIGHT = "60vh";
// Load the next page when this close to the bottom (px).
const LOAD_AHEAD = 500;

// Every column has a set width, so the pinned header and the rows always line
// up. The table fills the page when the columns fit and scrolls sideways when
// they don't. Cells never break mid-word: long text is cut with "…" and shown
// in full on hover.
const OneLine = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

// Tags and lists may wrap, but only between tags.
const TagCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  /* A long list name is cut with "…" and shown in full on hover, rather than
     spilling out of its column. */
  .arco-tag {
    max-width: 100%;
    min-width: 0;
  }
  .arco-tag-content {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Arco sets the table exactly as wide as its columns; when they're narrower
// than the page, stretch it (header and rows alike) to fill the space.
const TableWrap = styled.div`
  .arco-table-header table,
  .arco-table-body table {
    min-width: 100%;
  }
`;

const CHECKBOX_WIDTH = 56;
const WIDTH_BY_KEY = { name: 200, email: 240 };
const WIDTH_BY_KIND = { status: 130, tags: 180, lists: 180, signed_up: 140, date: 140, number: 120 };
const columnWidth = (col) => WIDTH_BY_KEY[col.key] || WIDTH_BY_KIND[col.kind] || 160;

// Width of text as the table draws it (14px, the page's font), in px.
let measureCtx;
function textWidth(text, weight) {
    if (!measureCtx) {
        try {
            measureCtx = document.createElement("canvas").getContext("2d");
        } catch (e) {
            measureCtx = null;
        }
        if (!measureCtx) return text.length * 8; // no canvas (tests): rough guess
    }
    const family = window.getComputedStyle(document.body).fontFamily || "sans-serif";
    measureCtx.font = `${weight || 400} 14px ${family}`;
    return measureCtx.measureText(text).width;
}

// Kinds whose cells are plain text, so their width can be measured.
const MEASURABLE = new Set(["text", "custom", "date", "signed_up", "number"]);
const CELL_PADDING = 32; // 16px each side
const SORTER_SPACE = 22; // the sort arrows beside a header title
const MIN_FIT = 90;

// The pinned first column shrinks to fit its widest value (or its title),
// up to its usual width; anything longer is cut with "…".
function fittedWidth(col, values) {
    const max = columnWidth(col);
    if (!MEASURABLE.has(col.kind)) return max;
    let widest = textWidth(col.title, 500) + (col.sort ? SORTER_SPACE : 0);
    values.forEach((v) => {
        if (v != null && v !== "") widest = Math.max(widest, textWidth(String(v)));
    });
    return Math.min(max, Math.max(MIN_FIT, Math.ceil(widest + CELL_PADDING + 2)));
}


export default function InfiniteScrollContactTable({ contactItems, selected, setSelected, setSelectAllLocal, columnDefs, sort, onSortChange, contactListFilterId, hasNextPage, loadItemsLoading, totalCount, loadMore, loadContactsError, handleContactRowClick, handleContactAddClick, onRemoveTag, emptyState = "none" }) {

    const handleSelectAllClick = (selectedAll) => {
        if (selectedAll) {
            const newSelecteds = contactItems.map((n) => n.id);
            setSelected(newSelecteds);
            setSelectAllLocal(true);
        } else {
            setSelected([]);
            setSelectAllLocal(false);
        }
    };

    // Arco's scroll area is its .arco-table-body, found once the table is on
    // the page. Refs hold the latest props so the scroll listener never goes stale.
    const wrapRef = React.useRef(null);
    const latest = React.useRef({});
    latest.current = { hasNextPage, loadItemsLoading, loadContactsError, loadMore };
    const maybeLoadMore = React.useCallback(() => {
        const body = wrapRef.current && wrapRef.current.querySelector(".arco-table-body");
        const { hasNextPage, loadItemsLoading, loadContactsError, loadMore } = latest.current;
        if (!body || !hasNextPage || loadItemsLoading || loadContactsError) return;
        if (body.scrollHeight - body.scrollTop - body.clientHeight < LOAD_AHEAD) loadMore();
    }, []);
    React.useEffect(() => {
        const body = wrapRef.current && wrapRef.current.querySelector(".arco-table-body");
        if (!body) return undefined;
        body.addEventListener("scroll", maybeLoadMore, { passive: true });
        return () => body.removeEventListener("scroll", maybeLoadMore);
    });
    // After each load: if the rows still don't fill the box, fetch more.
    React.useEffect(maybeLoadMore, [contactItems, hasNextPage, loadItemsLoading, maybeLoadMore]);

    const renderTags = (tags, record) =>
        (tags || []).map((t) => (
            <Tag
                key={t.id}
                size="small"
                color={t.color || "gray"}
                closable={!!onRemoveTag}
                onClose={() => onRemoveTag && onRemoveTag(record.id, t.id)}
            >
                {t.name}
            </Tag>
        ));

    const renderLists = (subscriptions) =>
        (subscriptions || []).map((subscription, index) => (
            <Tag
                key={index}
                size="small"
                color="arcoblue"
                bordered={!(subscription.contact_list_id == contactListFilterId)}
                title={subscription.list_name}
            >
                {subscription.list_name}
            </Tag>
        ));

    const renderCell = (col, record) => {
        switch (col.kind) {
            case "tags":
                return <TagCell>{renderTags(record.tags, record)}</TagCell>;
            case "lists":
                return <TagCell>{renderLists(record.subscriptions)}</TagCell>;
            case "text":
            case "custom": {
                const value = renderValue(col, record);
                return <OneLine title={value == null ? undefined : String(value)}>{value}</OneLine>;
            }
            default:
                return <OneLine>{renderValue(col, record)}</OneLine>;
        }
    };

    const renderValue = (col, record) => {
        switch (col.kind) {
            case "status": {
                const meta = suppressionMeta(record.suppression_reason);
                return <Tag size="small" color={meta.color}>{meta.label}</Tag>;
            }
            case "signed_up":
                return formatDate(signedUpDate(record, contactListFilterId));
            case "date":
                return formatDate(record[col.field]);
            case "number":
                return record[col.field] ?? 0;
            case "custom":
                return formatCustom((record.custom_fields || {})[col.cfKey], col.fieldType);
            default:
                return record[col.field];
        }
    };

    const { key: sortKey, desc: sortDesc } = parseSort(sort);
    const firstCol = (columnDefs || [])[0];
    const firstWidth = React.useMemo(
        () => firstCol && fittedWidth(firstCol, (contactItems || []).map((r) => renderValue(firstCol, r))),
        // renderValue only reads the list filter besides the row itself
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [firstCol, contactItems, contactListFilterId]
    );
    const widthOf = (col, idx) => (idx === 0 ? firstWidth : columnWidth(col));

    const columns = (columnDefs || []).map((col, idx) => ({
        key: col.key,
        // The first column, whichever it is, stays in view while scrolling
        // sideways (with the checkboxes).
        fixed: idx === 0 ? "left" : undefined,
        dataIndex: col.key,
        title: <OneLine>{col.title}</OneLine>,
        width: widthOf(col, idx),
        align: col.kind === "number" ? "right" : undefined,
        render: (_, record) => renderCell(col, record),
        ...(col.sort ? {
            sorter: true,
            sortDirections: col.kind === "signed_up" || col.kind === "date" ? ["descend", "ascend"] : ["ascend", "descend"],
            sortOrder: col.sort === sortKey ? (sortDesc ? "descend" : "ascend") : undefined,
        } : {}),
    }));

    // Header click -> new server sort. Clearing a sort (third click) goes back
    // to the default rather than an arbitrary order.
    const handleTableChange = (_pagination, sorter) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const col = (columnDefs || []).find((c) => c.key === (s && s.field));
        if (!onSortChange) return;
        onSortChange(col && col.sort && s.direction ? formatSort(col.sort, s.direction === "descend") : undefined);
    };

    const rowSelection = {
        // Arco's default is 40px, which crowds the checkbox against the edge.
        columnWidth: CHECKBOX_WIDTH,
        fixed: true,
        selectedRowKeys: selected,
        onChange: (selectedRowKeys) => {
            setSelected(selectedRowKeys);
        },
        onSelectAll: (selectedAll) => {
            handleSelectAllClick(selectedAll);
        },
    };

    const rows = contactItems && 'map' in contactItems ? contactItems : [];

    return (
        <>
            <TableWrap ref={wrapRef}>
                <Table
                    size="small"
                    rowKey="id"
                    columns={columns}
                    data={rows}
                    onChange={handleTableChange}
                    rowSelection={rowSelection}
                    pagination={false}
                    scroll={{ x: CHECKBOX_WIDTH + (columnDefs || []).reduce((sum, col, idx) => sum + widthOf(col, idx), 0), y: TABLE_HEIGHT }}
                    noDataElement={
                        emptyState === "loading" ? <EmptyState description="Loading contacts…" /> :
                        emptyState === "filtered" ? <EmptyState description="No contacts match this search or filter" /> :
                        <EmptyState description="No contacts yet">
                            <Button type="primary" style={{ marginTop: 12 }} onClick={handleContactAddClick}>
                                Add your first contact
                            </Button>
                        </EmptyState>
                    }
                    onRow={(row) => ({
                        style: { cursor: "pointer" },
                        onClick: (e) => {
                            if (
                                e.target.type === "checkbox" ||
                                (typeof e.target.className === "string" &&
                                    e.target.className.indexOf("checkbox") > -1)
                            ) {
                                return;
                            }
                            handleContactRowClick(row);
                        },
                    })}
                />
                {loadItemsLoading && <i>Loading...</i>}
            </TableWrap>
            <div>
                <Text>Displayed records {contactItems.length < totalCount ? contactItems.length : totalCount} out of {totalCount} </Text>
            </div>
        </>
    )
}
